/**
 * Shared queries for account-stable admin reference data.
 *
 * The permission catalog, the per-account role catalogs and the
 * per-account location list change rarely but were historically fetched
 * by every modal open (UserModal, InviteUserModal, LocationAccessGrid
 * each kept their own load effect / instance cache). These hooks put
 * that data in the shared query cache with a 5-minute staleTime so a
 * whole admin session shares one fetch per dataset.
 *
 * Normalization lives in the query functions — most importantly the
 * role-list unwrap quirk (`item.role || item` + permissions merge +
 * seen-ID dedupe) that used to be copy/pasted into all three consumers.
 * Consumers receive the normalized shape from the cache and only do
 * their own presentation-level mapping.
 */
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { adminService } from '../services/auth';
import type { AccountLocationsPageParams } from '../services/adminApi/locationsDepartments';
import { queryKeys } from './queryKeys';
import { useRoleLockViewer } from './roleLockScope';

/** Account-stable reference data: roles / permissions / locations. */
const REFERENCE_STALE_TIME_MS = 5 * 60 * 1000;

export interface NormalizedRole {
  id: string;
  name: string;
  permissions: any[];
  /** Whether the signed-in person may give this role (only when the API sent it: with account_id). */
  assignable?: boolean;
  /** Why not: 'not_held' | 'staff_only' | 'no_assign_right'; null when assignable. */
  not_assignable_reason?: string | null;
  [key: string]: any;
}

/**
 * The API returns role lists as `[{ role: {...}, permissions: [...],
 * assignable, not_assignable_reason }]` (sometimes flat). Unwrap, merge the
 * permissions and the assignable flags onto the role object and dedupe by
 * ID (safety net in case of duplicate data). Flags the API didn't send stay
 * absent, so an older API locks nothing.
 */
export function normalizeRoleList(rolesResponse: any[]): NormalizedRole[] {
  const mapped = (Array.isArray(rolesResponse) ? rolesResponse : []).map((item: any) => {
    const role = item.role || item;
    const out: NormalizedRole = {
      ...role,
      permissions: item.permissions || role.permissions || [],
    };
    if (typeof item.assignable === 'boolean') {
      out.assignable = item.assignable;
      out.not_assignable_reason = item.not_assignable_reason ?? null;
    }
    return out;
  });

  const seenIds = new Set<string>();
  return mapped.filter((role: any) => {
    if (seenIds.has(role.id)) {
      return false;
    }
    seenIds.add(role.id);
    return true;
  });
}

interface ReferenceQueryOptions {
  /** Gate the fetch exactly like the legacy effects did (modal open, account present, …). */
  enabled?: boolean;
}

interface AccountRolesOptions extends ReferenceQueryOptions {
  /** 'account' = admin roles (UserModal/InviteUserModal), 'location' = daily-operation roles (LocationAccessGrid). */
  roleType: 'account' | 'location';
  /**
   * A store of the account: location roles come back marked `assignable`
   * for that store (what the person holds there). Without it they are
   * marked account-wide, which never unlocks more than the store would.
   */
  locationId?: string;
}

/**
 * Role catalog for an account, normalized + deduped. Keyed per
 * account + role_type (+ store) so account-admin roles, location roles and
 * each store's answer cache independently.
 */
export function useAccountRoles(accountId: string, { roleType, locationId, enabled = true }: AccountRolesOptions) {
  const viewer = useRoleLockViewer();
  return useQuery({ ...accountRolesQuery(accountId, roleType, locationId, viewer), enabled: enabled && !!accountId });
}

/**
 * The same query as a plain object (for useQueries). Keyed by the signed-in
 * user too: the lock flags are that person's answer (./roleLockScope).
 */
export function accountRolesQuery(accountId: string, roleType: 'account' | 'location', locationId?: string, viewer?: string | null) {
  return {
    queryKey: queryKeys.roles.byAccount(accountId, {
      role_type: roleType,
      ...(locationId ? { location_id: locationId } : {}),
      viewer: viewer ?? null,
    }),
    queryFn: async () => {
      const rolesResponse = await adminService.listRoles({
        // Account-type roles were historically requested with the
        // legacy scope filter as well; keep it for identical results.
        ...(roleType === 'account' ? { scope: 'account' as const } : {}),
        role_type: roleType,
        account_id: accountId,
        ...(locationId ? { location_id: locationId } : {}),
        limit: 100,
        offset: 0,
      });
      return normalizeRoleList(rolesResponse);
    },
    staleTime: REFERENCE_STALE_TIME_MS,
  };
}

/** Role id → why it can't be given (null = it can). */
export type RoleLocks = Record<string, string | null>;

/** The lock of each role in a list that carries the flags; undefined when it doesn't (an older API). */
export function roleLocksOf(roles: NormalizedRole[] | undefined): RoleLocks | undefined {
  if (!roles || !roles.some((r) => typeof r.assignable === 'boolean')) return undefined;
  const out: RoleLocks = {};
  for (const r of roles) {
    if (typeof r.assignable === 'boolean') out[r.id] = r.assignable ? null : (r.not_assignable_reason ?? 'not_held');
  }
  return out;
}

/**
 * Which location roles the signed-in person may give at ONE store. Fetched
 * only when `enabled` (a picker opened), cached 5 minutes per store, so a
 * grid of many stores asks only for the ones someone actually opens.
 */
export function useStoreRoleLocks(accountId: string, locationId: string | undefined, { enabled = true }: ReferenceQueryOptions = {}) {
  const q = useAccountRoles(accountId, { roleType: 'location', locationId, enabled: enabled && !!locationId });
  return { locks: roleLocksOf(q.data), isLoading: q.isLoading && q.fetchStatus !== 'idle', isError: q.isError };
}

/** A bigger group isn't checked store by store; the preview still says where a role lands. */
export const MAX_GROUP_STORE_CHECKS = 30;

/**
 * Which location roles the signed-in person can't give at ANY store of a
 * group (one request per store, shared with the single-store cache). A role
 * locked at some stores only is not locked here: the group's preview lists
 * the stores it skips. undefined while loading, for an older API, or for a
 * group too big to check.
 */
export function useGroupRoleLocks(accountId: string, storeIds: string[], { enabled = true }: ReferenceQueryOptions = {}) {
  const viewer = useRoleLockViewer();
  const on = enabled && !!accountId && storeIds.length > 0 && storeIds.length <= MAX_GROUP_STORE_CHECKS;
  const results = useQueries({
    queries: (on ? storeIds : []).map((id) => ({ ...accountRolesQuery(accountId, 'location', id, viewer), enabled: on })),
  });
  const isLoading = on && results.some((r) => r.isLoading);
  if (!on || isLoading || results.some((r) => !r.data)) return { locks: undefined as RoleLocks | undefined, isLoading };
  const perStore = results.map((r) => roleLocksOf(r.data));
  if (perStore.some((l) => !l)) return { locks: undefined as RoleLocks | undefined, isLoading: false };
  return { locks: lockedEverywhere(perStore as RoleLocks[]), isLoading: false };
}

/** A role is locked for a group only when it is locked at every store; the reason that tells most. */
export function lockedEverywhere(perStore: RoleLocks[]): RoleLocks {
  const out: RoleLocks = {};
  const ids = new Set(perStore.flatMap((l) => Object.keys(l)));
  for (const id of ids) {
    const reasons = perStore.map((l) => (id in l ? l[id] : null));
    if (reasons.some((r) => !r)) {
      out[id] = null;
      continue;
    }
    out[id] = reasons.includes('staff_only') ? 'staff_only' : reasons.includes('not_held') ? 'not_held' : (reasons[0] ?? 'not_held');
  }
  return out;
}

/**
 * Full permission catalog (account-stable; ~500 rows). Returns the raw
 * catalog rows — consumers map to their own display shape (the two
 * existing consumers disagree on label fallbacks, so the cache keeps
 * the canonical API shape).
 */
export function usePermissionsCatalog({ enabled = true }: ReferenceQueryOptions = {}) {
  return useQuery({
    queryKey: queryKeys.permissions.catalog,
    queryFn: async () => {
      const response = await adminService.listPermissions(500, 0);
      return response || [];
    },
    staleTime: REFERENCE_STALE_TIME_MS,
    enabled,
  });
}

/**
 * Locations of an account (raw API shape, soft-deleted included as the
 * endpoint returns them — consumers filter/map for display).
 */
export function useAccountLocations(accountId: string, { enabled = true }: ReferenceQueryOptions = {}) {
  return useQuery({
    // Distinct from the app selector's `byAccount` key: this query returns a
    // raw Location[], the selector caches a shaped `{ options }` object. Sharing
    // one key made React Query serve the selector's object here, which
    // `accountLocations` (Array.isArray ? … : []) collapsed to an empty grid.
    queryKey: queryKeys.locations.detailByAccount(accountId),
    queryFn: async () => {
      const locationsList = await adminService.getLocations(accountId);
      // Defensive unwrap kept from the legacy loaders. Verify the inner
      // `.data` is itself an array — some account payloads wrap it in a
      // (non-array) envelope, which previously slipped through `?? []` and
      // crashed `.filter`/`.map` consumers.
      if (Array.isArray(locationsList)) return locationsList;
      const inner = (locationsList as any)?.data;
      return Array.isArray(inner) ? inner : [];
    },
    staleTime: REFERENCE_STALE_TIME_MS,
    enabled: enabled && !!accountId,
  });
}

/**
 * Account ids and names (every live account for a system admin, the token's
 * account otherwise) — for pickers and name lookups. Shares its cache with
 * useSystemAdminAccounts.
 */
export function useAccountSummaries({ enabled = true }: ReferenceQueryOptions = {}) {
  return useQuery({ ...accountSummariesQuery(), enabled });
}

/** The same query for imperative loaders (`queryClient.fetchQuery`). */
export function accountSummariesQuery() {
  return {
    queryKey: queryKeys.accounts.summaries,
    queryFn: () => adminService.getAccountSummaries(),
    staleTime: REFERENCE_STALE_TIME_MS,
  };
}

/**
 * Every location of an account as id + name, from one request — what pickers,
 * filters and name lookups need. Keyed under detailByAccount, so whatever
 * refreshes the account's locations after a change refreshes the names too.
 */
export function useAccountLocationNames(
  accountId: string | undefined,
  { includeDeleted = false, enabled = true }: ReferenceQueryOptions & { includeDeleted?: boolean } = {}
) {
  return useQuery({ ...accountLocationNamesQuery(accountId ?? '', includeDeleted), enabled: enabled && !!accountId });
}

/**
 * The same query for code that loads imperatively (`queryClient.fetchQuery`),
 * so it reads and fills the hook's cache instead of fetching again.
 */
export function accountLocationNamesQuery(accountId: string, includeDeleted = false) {
  return {
    queryKey: [...queryKeys.locations.detailByAccount(accountId), 'names', { includeDeleted }] as const,
    queryFn: () => adminService.getAccountLocationNames(accountId, { includeDeleted }),
    staleTime: REFERENCE_STALE_TIME_MS,
  };
}

/**
 * One page of an account's locations, filtered on the server — the account
 * admin's Locations list. Its key sits under detailByAccount, so everything
 * that refreshes the account's locations after a change refreshes this too.
 * The previous page stays on screen while the next one loads.
 */
export function useAccountLocationsPage(
  accountId: string,
  params: AccountLocationsPageParams,
  { enabled = true }: ReferenceQueryOptions = {}
) {
  return useQuery({
    queryKey: [...queryKeys.locations.detailByAccount(accountId), 'page', params] as const,
    queryFn: () => adminService.getAccountLocationsPage(accountId, params),
    placeholderData: keepPreviousData,
    staleTime: REFERENCE_STALE_TIME_MS,
    enabled: enabled && !!accountId,
  });
}

/**
 * One account's full row (GET /accounts/:id), for the few screens that need a
 * setting off it (allow_service_splits) — instead of loading every account on
 * the platform to read one flag. Default staleTime, so a flag changed in
 * account settings shows up as soon as the screen mounts again.
 */
export function useAccountDetail(accountId: string | null | undefined, { enabled = true }: ReferenceQueryOptions = {}) {
  return useQuery({
    queryKey: queryKeys.accounts.detail(accountId ?? ''),
    queryFn: () => adminService.getAccount(accountId as string),
    enabled: enabled && !!accountId,
  });
}
